/**
 * Centralized membership refresh after Stripe subscription changes.
 * Single source of truth for post-checkout and post-portal UI updates.
 */

import type { MembershipTier } from "@/types/profile";
import { apiUrl } from "@/lib/api-base";

export interface RefreshMembershipResult {
  tier: MembershipTier;
  hasActiveSubscription: boolean;
  cancelAtPeriodEnd?: boolean;
  scheduledDowngradeTo?: "premium" | null;
  currentPeriodEnd?: string | null;
}

const REFRESH_DEDUPE_WINDOW_MS = 2000;
let inFlightRefresh: Promise<RefreshMembershipResult | null> | null = null;
let lastRefreshResult: RefreshMembershipResult | null = null;
let lastRefreshAtMs = 0;

/**
 * Refreshes membership state from Stripe/DB and updates auth + profile stores.
 * Call after checkout success or returning from billing portal.
 * Always runs billing sync first to ensure Stripe (source of truth) updates DB.
 */
export async function refreshMembershipState(): Promise<RefreshMembershipResult | null> {
  const now = Date.now();

  // Share one refresh pipeline across near-simultaneous callers.
  if (inFlightRefresh) return inFlightRefresh;
  if (lastRefreshResult && now - lastRefreshAtMs < REFRESH_DEDUPE_WINDOW_MS) {
    return lastRefreshResult;
  }

  const runRefresh = async (): Promise<RefreshMembershipResult | null> => {
    try {
      // 1. Sync from Stripe -> DB (source of truth for portal changes)
      const syncRes = await fetch(apiUrl("/api/billing/sync"), {
        method: "POST",
        credentials: "include",
      });
      let tierFromSync: MembershipTier | null = null;
      if (syncRes.ok) {
        const syncData = await syncRes.json();
        if (syncData.synced && syncData.subscription?.plan_tier) {
          tierFromSync = (syncData.subscription.plan_tier || "free").toLowerCase() as MembershipTier;
        }
      }

      // 2. Fetch membership status (reads from DB, now updated by sync)
      const statusRes = await fetch(apiUrl("/api/membership/status"), { credentials: "include" });
      if (!statusRes.ok) return null;

      const data = await statusRes.json();
      let tier = ((data.tier || data.status || "free") as string).toLowerCase() as MembershipTier;
      const hasActiveSubscription = data.hasActiveSubscription === true;
      const cancelAtPeriodEnd = data.cancelAtPeriodEnd === true;
      const scheduledDowngradeTo = data.scheduledDowngradeTo === "premium" ? "premium" as const : null;
      const currentPeriodEnd = data.currentPeriodEnd ?? null;

      // Prefer tier from sync when available (Stripe is source of truth for portal upgrades)
      if (tierFromSync && tierFromSync !== "free") {
        tier = tierFromSync;
      }

      // 3. Update auth store
      const authStore = (await import("@/stores/auth-store")).useAuthStore.getState();
      authStore.updateMembershipTier(tier);

      // 4. Refresh profile (permissions depend on profile.membershipTier)
      const userId = authStore.user?.id;
      if (userId) {
        const { useProfileStore } = await import("@/stores/profile-store");
        await useProfileStore.getState().fetchCurrentProfile(userId, true);
      }

      if (process.env.NODE_ENV === "development") {
        console.log("[refreshMembershipState] Result:", {
          tier,
          tierFromSync,
          hasActiveSubscription,
          tierSource: tierFromSync ? "sync" : "membership/status",
        });
      }

      const result = { tier, hasActiveSubscription, cancelAtPeriodEnd, scheduledDowngradeTo, currentPeriodEnd };
      lastRefreshResult = result;
      lastRefreshAtMs = Date.now();
      return result;
    } catch (error) {
      console.warn("[refreshMembershipState] Error:", error);
      return null;
    } finally {
      inFlightRefresh = null;
    }
  };

  inFlightRefresh = runRefresh();
  return inFlightRefresh;
}
